// Binary / number-base calculator logic.
// All math uses BigInt for precision; bitwise ops are defined on
// two's-complement of a chosen bit width (default 32) for negatives.

export type Base = 2 | 8 | 10 | 16;

export const BASE_NAMES: Record<Base, { en: string; bn: string; short: string }> = {
  2: { en: "Binary", bn: "বাইনারি", short: "BIN" },
  8: { en: "Octal", bn: "অক্টাল", short: "OCT" },
  10: { en: "Decimal", bn: "ডেসিমেল", short: "DEC" },
  16: { en: "Hexadecimal", bn: "হেক্সাডেসিমেল", short: "HEX" },
};

const ALLOWED: Record<Base, RegExp> = {
  2: /^-?[01]+$/,
  8: /^-?[0-7]+$/,
  10: /^-?[0-9]+$/,
  16: /^-?[0-9a-fA-F]+$/,
};

export function isValidForBase(value: string, base: Base): boolean {
  const v = value.trim();
  if (!v) return false;
  return ALLOWED[base].test(v);
}

/** Sanitize: strip characters not allowed in the given base (keeps a leading minus). */
export function sanitizeForBase(value: string, base: Base): string {
  const sign = value.trim().startsWith("-") ? "-" : "";
  const stripped = value.replace(/^-/, "");
  const allowed: Record<Base, RegExp> = {
    2: /[^01]/g,
    8: /[^0-7]/g,
    10: /[^0-9]/g,
    16: /[^0-9a-fA-F]/g,
  };
  return sign + stripped.replace(allowed[base], "");
}

/** Parse a string in given base into a BigInt. Throws on invalid input. */
export function parseBig(value: string, base: Base): bigint {
  const v = value.trim();
  if (!isValidForBase(v, base)) {
    throw new Error(`Invalid ${BASE_NAMES[base].en.toLowerCase()} value: "${value}"`);
  }
  const negative = v.startsWith("-");
  const body = negative ? v.slice(1) : v;
  let result = 0n;
  const b = BigInt(base);
  for (const ch of body.toUpperCase()) {
    const digit = ch >= "0" && ch <= "9" ? ch.charCodeAt(0) - 48 : ch.charCodeAt(0) - 55; // A=10
    if (digit < 0 || digit >= base) {
      throw new Error(`Invalid digit '${ch}' for base ${base}`);
    }
    result = result * b + BigInt(digit);
  }
  return negative ? -result : result;
}

/** Format a BigInt in the given base. Uppercase hex. */
export function formatBig(value: bigint, base: Base): string {
  const negative = value < 0n;
  const s = (negative ? -value : value).toString(base);
  return (negative ? "-" : "") + (base === 16 ? s.toUpperCase() : s);
}

/** Convert a value to all four bases simultaneously. */
export function toAllBases(value: bigint): Record<Base, string> {
  return {
    2: formatBig(value, 2),
    8: formatBig(value, 8),
    10: formatBig(value, 10),
    16: formatBig(value, 16),
  };
}

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------
export type ArithOp = "+" | "-" | "*" | "/";

export interface ArithResult {
  ok: true;
  decimal: bigint;
  remainder?: bigint; // for division
  bases: Record<Base, string>;
  steps: string[];
}
export interface ArithError {
  ok: false;
  error: { en: string; bn: string };
}

export function arithmetic(a: bigint, op: ArithOp, b: bigint): ArithResult | ArithError {
  let result: bigint;
  let remainder: bigint | undefined;
  const steps: string[] = [];
  switch (op) {
    case "+":
      result = a + b;
      steps.push(`${a} + ${b} = ${result}`);
      break;
    case "-":
      result = a - b;
      steps.push(`${a} − ${b} = ${result}`);
      break;
    case "*":
      result = a * b;
      steps.push(`${a} × ${b} = ${result}`);
      break;
    case "/":
      if (b === 0n) {
        return { ok: false, error: { en: "Division by zero.", bn: "শূন্য দিয়ে ভাগ করা যাবে না।" } };
      }
      result = a / b; // BigInt truncating division
      remainder = a - result * b;
      steps.push(`${a} ÷ ${b} = ${result} remainder ${remainder}`);
      break;
  }
  return { ok: true, decimal: result, remainder, bases: toAllBases(result), steps };
}

/** Step-by-step binary addition (column by column, right to left). */
export function binaryAdditionSteps(a: bigint, b: bigint): string[] {
  if (a < 0n || b < 0n) return [];
  const aBin = a.toString(2);
  const bBin = b.toString(2);
  const width = Math.max(aBin.length, bBin.length) + 1;
  const ap = aBin.padStart(width, "0");
  const bp = bBin.padStart(width, "0");
  const lines: string[] = [];
  lines.push(`  ${ap}`);
  lines.push(`+ ${bp}`);
  lines.push("  " + "-".repeat(width));
  let carry = 0;
  const out: string[] = [];
  const carryRow: string[] = [];
  for (let i = width - 1; i >= 0; i--) {
    const da = ap.charCodeAt(i) - 48;
    const db = bp.charCodeAt(i) - 48;
    const sum = da + db + carry;
    out.unshift(String(sum & 1));
    carry = sum >> 1;
    carryRow.unshift(carry ? "1" : " ");
  }
  lines.push("  " + out.join(""));
  if (carryRow.some((c) => c === "1")) {
    lines.push(`carry: ${carryRow.join("")}`);
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Bitwise — operate on two's-complement of fixed width for negatives.
// ---------------------------------------------------------------------------
export type BitOp = "AND" | "OR" | "XOR" | "NOT" | "SHL" | "SHR";

export const BIT_WIDTHS = [8, 16, 32, 64] as const;
export type BitWidth = (typeof BIT_WIDTHS)[number];

/** Two's-complement representation of a BigInt at given width. Returns unsigned BigInt. */
export function toTwosComplement(value: bigint, width: BitWidth): bigint {
  const mod = 1n << BigInt(width);
  let v = value % mod;
  if (v < 0n) v += mod;
  return v;
}

/** Convert unsigned BigInt back to signed at given width. */
export function fromTwosComplement(value: bigint, width: BitWidth): bigint {
  const mod = 1n << BigInt(width);
  const half = 1n << BigInt(width - 1);
  const v = value & (mod - 1n);
  return v >= half ? v - mod : v;
}

/** Format BigInt as fixed-width binary string (using two's complement for negatives). */
export function toFixedBinary(value: bigint, width: BitWidth): string {
  return toTwosComplement(value, width).toString(2).padStart(width, "0");
}

export interface BitResult {
  ok: true;
  width: BitWidth;
  aBin: string;
  bBin: string | null; // null for NOT
  resultBin: string;
  resultDecimal: bigint;
  bases: Record<Base, string>;
  op: BitOp;
  shiftAmount?: number;
}
export interface BitError {
  ok: false;
  error: { en: string; bn: string };
}

export function bitwise(
  a: bigint,
  op: BitOp,
  b: bigint,
  width: BitWidth = 32,
): BitResult | BitError {
  const ua = toTwosComplement(a, width);
  let ub = b;
  let resultUnsigned: bigint;
  let shift: number | undefined;
  switch (op) {
    case "AND":
      resultUnsigned = ua & toTwosComplement(b, width);
      break;
    case "OR":
      resultUnsigned = ua | toTwosComplement(b, width);
      break;
    case "XOR":
      resultUnsigned = ua ^ toTwosComplement(b, width);
      break;
    case "NOT": {
      const mask = (1n << BigInt(width)) - 1n;
      resultUnsigned = ua ^ mask;
      ub = 0n;
      break;
    }
    case "SHL": {
      shift = Number(b);
      if (shift < 0 || shift >= width) {
        return {
          ok: false,
          error: {
            en: `Shift amount must be between 0 and ${width - 1}.`,
            bn: `শিফট মান ০ থেকে ${width - 1} এর মধ্যে হতে হবে।`,
          },
        };
      }
      const mask = (1n << BigInt(width)) - 1n;
      resultUnsigned = (ua << BigInt(shift)) & mask;
      break;
    }
    case "SHR": {
      shift = Number(b);
      if (shift < 0 || shift >= width) {
        return {
          ok: false,
          error: {
            en: `Shift amount must be between 0 and ${width - 1}.`,
            bn: `শিফট মান ০ থেকে ${width - 1} এর মধ্যে হতে হবে।`,
          },
        };
      }
      resultUnsigned = ua >> BigInt(shift); // logical (unsigned) shift on the unsigned form
      break;
    }
  }
  const resultDecimal = fromTwosComplement(resultUnsigned, width);
  return {
    ok: true,
    width,
    aBin: toFixedBinary(a, width),
    bBin: op === "NOT" ? null : op === "SHL" || op === "SHR" ? null : toFixedBinary(b, width),
    resultBin: toFixedBinary(resultDecimal, width),
    resultDecimal,
    bases: toAllBases(resultDecimal),
    op,
    shiftAmount: shift,
  };
}

// ---------------------------------------------------------------------------
// Educational: decimal-to-binary by repeated division
// ---------------------------------------------------------------------------
export interface DivisionStep {
  dividend: bigint;
  quotient: bigint;
  remainder: bigint;
}

/** Generate the repeated-division steps from a non-negative decimal to binary. */
export function decimalToBinarySteps(n: bigint, base: Base = 2): DivisionStep[] {
  if (n < 0n) n = -n;
  if (n === 0n) return [{ dividend: 0n, quotient: 0n, remainder: 0n }];
  const steps: DivisionStep[] = [];
  const b = BigInt(base);
  let cur = n;
  while (cur > 0n) {
    const q = cur / b;
    const r = cur - q * b;
    steps.push({ dividend: cur, quotient: q, remainder: r });
    cur = q;
  }
  return steps;
}

/** Generate positional expansion steps for binary→decimal: e.g. 1·2³ + 0·2² + 1·2¹ + 1·2⁰ = 11 */
export function binaryToDecimalSteps(bin: string): { tex: string; total: bigint } {
  const clean = bin.replace(/^-/, "").replace(/[^01]/g, "");
  if (!clean) return { tex: "", total: 0n };
  const parts: string[] = [];
  let total = 0n;
  const len = clean.length;
  for (let i = 0; i < len; i++) {
    const bit = clean.charCodeAt(i) - 48;
    const power = len - 1 - i;
    if (bit === 1) {
      total += 1n << BigInt(power);
      parts.push(`1·2^{${power}}`);
    } else {
      parts.push(`0·2^{${power}}`);
    }
  }
  return { tex: `${parts.join(" + ")} = ${total}`, total };
}
